import React, { useEffect, useRef, useState } from "react";
import { useRoute } from "react-router5";
import { connect } from "react-redux";

import {
  aggregateByRoots,
  aggregateBySubroots,
  loadThemedTiledLayer,
  aggregateRangeSettings,
  upgradeSettingLayers,
  handleClick,
  buildUpdator,
} from "./shared";

import { buildLegend } from "./legend";

function aggregateByPOIGeo(nodes, new_zl, agg_range) {
  const lvl = ["level0", "level1"].find((x) => agg_range[x].includes(new_zl));

  switch (lvl) {
    case "level0":
      return aggregateByRoots(nodes);
    case "level1":
      return aggregateBySubroots(nodes);
    default:
      return nodes.slice();
  }
}

const makeOnZoomLevelChanged = (agg_range) => {
  return function onZoomLevelChanged(nodes, new_zl, prev_zl) {
    // aggs: geo_aggregations
    const new_nodes = aggregateByPOIGeo(nodes, new_zl, agg_range);

    return (
      new_nodes
        // remove aggs that dont have any posts associated to it (could be done at start?)
        .map((x) =>
          x.aggs ? { ...x, aggs: x.aggs.filter((y) => y.posts.length) } : x,
        )
        // only take points that either have posts associated to it
        // or who have aggregate that have
        .map((x) => {
          const agg_length = (x.aggs || []).reduce(
            (acc, x) => acc + x.posts.length,
            0,
          );
          const total_count = x.posts.length + agg_length;

          return { ...x, weight: total_count };
        })
        .filter((x) => x.weight)
    );
  };
};

function initializeChart(data, opts) {
  const {
    element,
    height,
    navigation,
    onClick,
    initial_theme,
    onZoomLevelChanged,
    router,
    layers: layers_from_settings,
    data: data_from_settings,
    legend,
  } = opts;

  let current_zoom = navigation.initialZoom;

  const new_nodes = onZoomLevelChanged(data.nodes, current_zoom, current_zoom);

  // will be updated by updateChart
  let mutable_data = data;
  let mutable_geo_filter = null;

  const theme_tiled_loader = loadThemedTiledLayer(initial_theme);

  const updator = buildUpdator(element);

  function onHoverChange(e) {
    const node = e.hoverNode;
    if (node?.data?.item) {
      updator.onHoverChange({
        hoverNode: node,
        force_text: node.data.item,
        x: e.x,
        y: e.y,
      });
    } else {
      updator.onHoverChange(e);
    }
  }

  let conf = {
    container: element,
    area: {
      height: height,
    },
    legend: {
      enabled: true,
    },
    background: { enabled: false }, // deactivate default tile map
    navigation: navigation,
    data: data_from_settings.concat([
      { preloaded: { ...data, nodes: new_nodes }, id: "defaultData" },
    ]),
    interaction: {
      selection: { tolerance: 0.3 },
    },
    events: {
      onHoverChange,
      onPointerDrag: (e, args) => {
        updator.update(e, args);
      },
      onSettingsChange: (e, args) => {
        updator.update(e, args);
      },
      onChartUpdate: (e, args) => {
        updator.update(e, args);
        theme_tiled_loader(e.chart);
      },
      onPositionChange: function (e, args) {
        updator.update(e, args);

        const prev_zl = current_zoom;
        const new_zl = chart.zoomLevel();

        if (new_zl !== prev_zl) {
          current_zoom = new_zl;

          // aggregate on root/subroot
          const new_nodes = onZoomLevelChanged(
            mutable_data.nodes,
            new_zl,
            prev_zl,
          );
          chart.replaceData(
            { ...mutable_data, nodes: new_nodes },
            "defaultData",
          );
        }
      },
      // https://zoomcharts.com/developers/en/geo-chart/api-reference/settings/events.html#doc_onClick
      onClick: function (e, args) {
        // update(e, args);

        // console.log(e);
        // console.log(args);

        // check for defaultData
        // otherwise delegate!
        const click_element = e.clickNode || e.clickLink;

        // leaflet-control-container
        function clickedOnZoom(node) {
          if (node === document.body) {
            return false;
          }
          if (node.classList.contains("leaflet-control-zoom")) {
            return true;
          }
          return clickedOnZoom(node.parentNode);
        }

        if (clickedOnZoom(e.target)) {
          return false;
        }

        if (click_element) {
          const settings = click_element.layer.layerSettings;

          if (settings.id === "default") {
            onClick(click_element.data);

            /*
	    // look for next step where we can see more details
	    let new_zoom = current_zoom <= 4 ? 5 : (current_zoom <= 7 ? 8 : current_zoom + 1);
	    chart.zoomLevel(new_zoom);
	    */
          } else {
            handleClick(click_element, router);
          }
        }
      },
    },
    layers: upgradeSettingLayers(layers_from_settings).concat([
      {
        id: "default",
        data: { id: "defaultData" },
        type: "items",
        aggregation: {
          enabled: false,
        },
        style: {
          node: { display: "roundtext" },
          // allObjectsStyleFunction: (nodes, links) => autoScaleNodes(chart, nodes, links),

          nodeStyleFunction: function (node) {
            // node.removed

            // node.label = node.data.label + ` (${node.data.weight})`

            // https://jsfiddle.net/api/post/jquery/1.12/?example=node-radius-by-population
            // node.label = ;
            // https://demo.zoomcharts.com/net-chart/examples/style/round-text-nodes.html
            // set by function
            node.radius = 13;
            node.label = node.data.weight;

            // node.radius = total_count; // set by autoScaleNodes
            // same as .tagGeo color var(--theme-secondary-pink1)
            node.fillColor = "rgb(242, 73, 134, 0.9)";

            if (node.data.id === mutable_geo_filter) {
              node.fillColor = "red";
            }

            node.lineWidth = 1;
            node.lineColor = "#333";
            node.cursor = "pointer";

            node.labelStyle = {
              backgroundStyle: {
                // fillColor:"#09c",
                // lineColor:"#333",
                lineWidth: 0,
              },
              textStyle: {
                fillColor: "white",
              },
            };
          },
        },
      },
    ]),
  };

  let chart = new window.GeoChart(conf);

  updator.setChart(chart);
  buildLegend(chart, element, legend);

  function updateChart(new_data) {
    mutable_data = new_data;

    const current_zoom = chart.zoomLevel();
    const new_nodes = onZoomLevelChanged(
      mutable_data.nodes,
      current_zoom,
      current_zoom,
    );

    const cb = () => {
      chart.replaceData({ ...mutable_data, nodes: new_nodes }, "defaultData");
    };
    const timeout = () => {
      if (!chart.leaflet()) {
        setTimeout(timeout, 50);
      } else {
        cb();
      }
    };

    timeout();
  }

  // https://gis.stackexchange.com/questions/283487/leaflet-setview-vs-panto/283683
  // https://zoomcharts.com/developers/en/geo-chart/api-reference/api.html
  function centerChart({ lat, lon }) {
    chart.leaflet().panTo([lat, lon]);
  }

  function setGeoFilter(id) {
    mutable_geo_filter = id;
    // chart.updateFilters();
    // chart.paintNow();
  }

  function onThemeUpdate(theme) {
    theme_tiled_loader(chart, theme);
  }

  return { chart, updateChart, centerChart, setGeoFilter, onThemeUpdate };
}

// raw points
// assign post to geos
function buildData({ geos }, posts) {
  const geo_list = geos
    .flatMap((root) => {
      return [root].concat(
        root.children.flatMap((subroot) => {
          return [subroot].concat(subroot.children);
        }),
      );
    })
    .filter((x) => x.lat && x.lon);

  const points = geo_list.map((x) => {
    return {
      ...x,
      type: "point",
      coordinates: [parseFloat(x.lon), parseFloat(x.lat)],
      label: x.item,
      posts: [],
    };
  });

  // assigning post to their geo point
  let geo_dict = {};
  points.forEach((g) => {
    geo_dict[g.id] = g;
  });

  posts.forEach((p) => {
    // FIXME: post may not contain a geographic point (still optional)
    if (p.geo) {
      geo_dict[p.geo.id].posts.push(p);
    }
  });

  // removing leaves w/ 0 posts associated
  const filtered_points = points.filter(
    (x) => !(x.posts.length === 0 && x.root && x.subroot),
  );

  return { nodes: filtered_points };
}

// build settings from backoffice params
function buildSettings(settings) {
  // default agg_range
  const agg_range = aggregateRangeSettings(settings.aggregate_range, {
    level0: [0, 1],
    level1: [2, 3],
  });

  return {
    navigation: { initialZoom: 3, ...(settings.navigation || {}) },
    onZoomLevelChanged: makeOnZoomLevelChanged(agg_range),
    layers: settings.layers || [],
    data: settings.data || [],
    legend: settings.legend || [],
  };
}

function CartoGeoPost({
  posts,
  center,
  onClick,
  currentTheme,
  map_settings /* , geo_filter */,
}) {
  const ref = useRef();
  const {
    route: { data },
    router,
  } = useRoute();

  const [chartAPI, setChartAPI] = useState();

  const settings = buildSettings(map_settings || {});

  // necessary as the onClick in the chart can't be changed
  const onClickRef = useRef();
  onClickRef.current = onClick;

  useEffect(() => {
    const d = buildData(data, posts);

    if (!chartAPI) {
      const opts = {
        ...settings,
        element: ref.current,
        height: ref.current.parentNode.offsetHeight,
        initial_theme: currentTheme,
        router,
        onClick: (geo) => {
          onClickRef.current(geo);
        },
      };

      const chartAPI = initializeChart(d, opts);
      setChartAPI(chartAPI);
    } else {
      chartAPI.updateChart(d);
    }
  }, [posts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chartAPI) {
      chartAPI.onThemeUpdate(currentTheme);
    }
  }, [chartAPI, currentTheme]);

  useEffect(() => {
    if (chartAPI && center) {
      chartAPI.centerChart(center.geo);
    }
  }, [chartAPI, center]);

  /*
  useEffect(() => {
    chart && geo_filter && chart.setGeoFilter(geo_filter.id)
  }, [geo_filter]); // eslint-disable-line react-hooks/exhaustive-deps
  */

  return <div ref={ref} />;
}

const mapStateToProps = (state) => ({ currentTheme: state.theme.value });

export default connect(mapStateToProps, null)(CartoGeoPost);
